import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { PhotoGrid } from "../components/PhotoGrid";
import { api } from "../lib/api";
import { useContent } from "../context/ContentContext";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "title", label: "Title A–Z" },
];

export default function Photography() {
  const { p } = useContent();
  const hdr = p("photography").header;
  const ui = p("photography").ui;
  const [cats, setCats] = useState([]);
  const [places, setPlaces] = useState([]);
  const [collections, setCollections] = useState([]);
  const [category, setCategory] = useState("all");
  const [place, setPlace] = useState("all");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    api.get("/photos/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
    api.get("/photos/places").then((r) => setPlaces(r.data.places || [])).catch(() => {});
    api.get("/photos/facets").then((r) => setCollections(r.data.collections || [])).catch(() => {});
  }, []);

  const chips = [{ slug: "all", label: "All" }, ...cats];
  const placeChips = [{ slug: "all", label: "All routes" }, ...places];
  const filters = {
    sort,
    ...(category !== "all" ? { category } : {}),
    ...(place !== "all" ? { place } : {}),
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="photography-page">
      <Seo title="Photography — CaminoSapiens" description="Original photography connected to long-distance walking, travel, landscape, pilgrimage, and observation." />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Photography" }]} />
      <div className="max-w-2xl mb-10">
        <p className="overline mb-4">{hdr.eyebrow}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight">{hdr.heading}</h1>
        <p className="text-[#4A4947] mt-6 leading-relaxed">
          {hdr.body}
        </p>
      </div>

      {collections.length > 0 && (
        <div className="mb-10" data-testid="photo-collections-strip">
          <div className="flex items-baseline justify-between mb-3">
            <p className="overline">{ui.collections_label}</p>
            <Link to="/photography/collections" className="text-sm link-underline text-[#4A4947]" data-testid="view-all-collections">{ui.view_all_collections_label}</Link>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {collections.map((c) => (
              <Link key={c} to={`/photography/collections/${encodeURIComponent(c)}`} data-testid={`collection-link-${c}`}
                className="text-sm text-[#9a9793] hover:text-[#1C1B1A] transition-colors">{c}</Link>
            ))}
          </div>
        </div>
      )}

      {/* Restrained curated filter / sort bar (derived from content, not raw tags) */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 mb-6 border-b border-[#E4E1DB] pb-5" data-testid="photo-toolbar">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {chips.map((c) => (
            <button key={c.slug} onClick={() => setCategory(c.slug)} data-testid={`photo-cat-${c.slug}`}
              className={`text-sm transition-colors ${category === c.slug ? "text-[#1C1B1A] border-b border-[#B35D4B]" : "text-[#9a9793] hover:text-[#4A4947]"}`}>
              {c.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-[#4A4947]">
          <span className="overline">{ui.sort_label}</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="photo-sort"
            className="bg-transparent border-b border-[#E4E1DB] py-1 pr-6 text-sm focus:outline-none focus:border-[#B35D4B] cursor-pointer">
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
      </div>

      {/* Second curated facet: Route & Region (works together with subject filters above) */}
      {places.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-12" data-testid="photo-place-toolbar">
          <span className="overline text-[#9a9793] mr-1">{ui.route_region_label}</span>
          {placeChips.map((p) => (
            <button key={p.slug} onClick={() => setPlace(p.slug)} data-testid={`photo-place-${p.slug}`}
              className={`text-sm transition-colors ${place === p.slug ? "text-[#1C1B1A] border-b border-[#B35D4B]" : "text-[#9a9793] hover:text-[#4A4947]"}`}>
              {p.label}
            </button>
          ))}
          <Link to="/photography/routes" className="text-sm link-underline text-[#4A4947] ml-1" data-testid="view-all-routes">{ui.all_routes_label}</Link>
        </div>
      )}

      <PhotoGrid filters={filters} />
    </div>
  );
}
